import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

const SID_KEY = 'wwhxp-analytics-sid';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private started = false;

  /** Wire up router events. Skips admin/auth routes — only public pages are tracked. */
  start(): void {
    if (this.started) return;
    this.started = true;

    // Track the initial page on bootstrap.
    queueMicrotask(() => this.trackCurrent(document.referrer || null));

    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe(e => this.track(e.urlAfterRedirects, null));
  }

  private trackCurrent(referrer: string | null): void {
    this.track(this.router.url || location.pathname, referrer);
  }

  private track(url: string, referrer: string | null): void {
    if (!url || this.isExcluded(url)) return;

    const body = {
      sessionId: this.getSessionId(),
      path: this.sanitizePath(url),
      referrer: referrer || document.referrer || null
    };

    // Fire-and-forget; never block UI or surface errors.
    this.http.post(`${environment.apiUrl}/analytics/track`, body, { responseType: 'text' as const })
      .subscribe({ next: () => {}, error: () => {} });
  }

  private isExcluded(url: string): boolean {
    const u = url.split('?')[0].split('#')[0].toLowerCase();
    return u.startsWith('/admin') || u.startsWith('/auth');
  }

  private sanitizePath(url: string): string {
    const path = url.split('#')[0].split('?')[0];
    return path.length > 500 ? path.substring(0, 500) : path;
  }

  private getSessionId(): string {
    try {
      let sid = sessionStorage.getItem(SID_KEY);
      if (!sid) {
        sid = this.uuid();
        sessionStorage.setItem(SID_KEY, sid);
      }
      return sid;
    } catch {
      return this.uuid();
    }
  }

  private uuid(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return (crypto as any).randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }
}
